/**
 * useCanvasNode — typed CanvasNode handle for a YDoc node.
 *
 * Usage:
 *   const node = useCanvasNode(id, 'characterDesigner')
 *   node?.writeInName('Alice')  // TypeScript checks field + value type
 *
 * In production, reads from the canvas context's Y.Map.
 * In tests (no canvas context), creates a Y.Map from the plain data record.
 */

import { useMemo } from 'react'
import * as Y from 'yjs'
import { useCanvasOptional } from '../components/canvas-v2/CanvasContext'
import {
  FE_TYPE_TO_BACKEND,
  NODE_STATE_FIELDS,
  YDOC_NODE_FIELDS,
  YMapRecord,
  YMapWrapper,
  bindCanvasNodeByFeType,
  type CanvasNodeByFeType,
  type FeNodeType,
  type YDocValue,
} from '../generated/nodeDataTypes'

function isRecordLike(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function toFallbackYDocValue(value: unknown): YDocValue {
  if (value === null || value === undefined) return value
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value
  }
  if (typeof File !== 'undefined' && value instanceof File) return undefined
  if (value instanceof YMapWrapper || value instanceof YMapRecord) {
    return toFallbackYDocValue(value.toJSON())
  }
  if (value instanceof Y.Map || value instanceof Y.Array) {
    return toFallbackYDocValue(value.toJSON())
  }
  if (Array.isArray(value)) {
    const yarr = new Y.Array<YDocValue>()
    yarr.push(value.map((item) => toFallbackYDocValue(item)))
    return yarr
  }
  if (typeof value === 'object') {
    const ymap = new Y.Map<YDocValue>()
    for (const [entryKey, entryValue] of Object.entries(value)) {
      ymap.set(entryKey, toFallbackYDocValue(entryValue))
    }
    return ymap
  }
  return String(value)
}

function isFallbackUiOnlyField(key: string): boolean {
  return key.startsWith('_') || key === 'status' || key === 'label' || key === 'name' ||
    key === 'error' || key === 'progress' || key === 'progressMessage' ||
    key === 'cacheKey' || key === 'projectId' || key === 'tainted' ||
    key === 'isInsideGroup' || key === 'initialFile'
}

export function useCanvasNode<K extends FeNodeType>(
  nodeId: string,
  _feType: K,
  data?: unknown,
): CanvasNodeByFeType[K] | undefined {
  const canvas = useCanvasOptional()

  return useMemo(() => {
    // Production: get Y.Map from canvas context
    if (nodeId && canvas?.getYNode) {
      const raw = canvas.getYNode(nodeId)
      if (raw) return bindCanvasNodeByFeType(raw, _feType) as CanvasNodeByFeType[K]
    }
    // Test fallback: wrap plain data as Y.Map
    if (isRecordLike(data)) {
      const doc = new Y.Doc()
      const ynode = doc.getMap('node') as Y.Map<YDocValue>
      ynode.set('type', _feType)
      const backendType = FE_TYPE_TO_BACKEND[_feType] ?? _feType
      const fieldSpecs = YDOC_NODE_FIELDS[backendType] ?? {}
      const stateFields = NODE_STATE_FIELDS[backendType]
      for (const [key, value] of Object.entries(data)) {
        if (isFallbackUiOnlyField(key)) {
          continue
        }
        if (typeof File !== 'undefined' && value instanceof File) {
          continue
        }
        if (!(key in fieldSpecs) && !stateFields?.has(key)) {
          throw new Error(`Unknown ${_feType} test fallback field ${key}`)
        }
        if (value !== undefined) {
          ynode.set(key, toFallbackYDocValue(value))
        }
      }
      return bindCanvasNodeByFeType(ynode, _feType) as CanvasNodeByFeType[K]
    }
    return undefined
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvas, nodeId, data])
}
